import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Utensils, ArrowLeft, Camera, Plus, Trash2, 
  Users, Check, X, Mail, Loader2, IndianRupee, Calculator, Clock
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { createExpense, scanReceipt, sendSplitRequest, getGroups } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useEffect } from 'react';

const CURRENCIES = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
];

const CreateSplit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [merchant, setMerchant] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState('');
  const [items, setItems] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [participantEmail, setParticipantEmail] = useState('');
  const [splitType, setSplitType] = useState('equal');
  const [scanning, setScanning] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [createdExpense, setCreatedExpense] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [cgst, setCgst] = useState(0);
  const [sgst, setSgst] = useState(0);
  const [tip, setTip] = useState(0);

  const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '₹';

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await getGroups();
      setGroups(res.data);
    } catch (error) {
      console.error('Failed to fetch groups');
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    setScanning(true);
    
    try {
      const res = await scanReceipt(file);
      const data = res.data;
      
      if (data.merchant) setMerchant(data.merchant);
      if (data.date) setDate(data.date);
      if (data.time) setTime(data.time);
      if (data.currency) setCurrency(data.currency);
      if (data.cgst) setCgst(parseFloat(data.cgst) || 0);
      if (data.sgst) setSgst(parseFloat(data.sgst) || 0);
      if (data.tip) setTip(parseFloat(data.tip) || 0);
      
      if (data.items && data.items.length > 0) {
        setItems(data.items.map((item, index) => ({
          id: Date.now() + index,
          name: item.name,
          price: parseFloat(item.price) || 0,
          quantity: parseInt(item.quantity) || 1,
          assignedTo: []
        })));
      }
      
      toast.success('Receipt scanned successfully! 🎉');
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to scan receipt. Try again or add items manually.');
    } finally {
      setScanning(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1
  });

  const itemsTotal = items.reduce((sum, item) => sum + (parseFloat(item.price) * (item.quantity || 1) || 0), 0);
  const taxesAndTip = (parseFloat(cgst) || 0) + (parseFloat(sgst) || 0) + (parseFloat(tip) || 0);
  const totalAmount = itemsTotal + taxesAndTip;

  const addItem = () => {
    setItems([...items, { id: Date.now(), name: '', price: 0, quantity: 1, assignedTo: [] }]);
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const toggleItemAssignment = (itemId, participantId) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const assignedTo = item.assignedTo.includes(participantId)
          ? item.assignedTo.filter(id => id !== participantId)
          : [...item.assignedTo, participantId];
        return { ...item, assignedTo };
      }
      return item;
    }));
  };

  const addParticipant = () => {
    if (!participantEmail || participants.some(p => p.email === participantEmail)) {
      return;
    }
    setParticipants([...participants, {
      id: `email_${Date.now()}`,
      email: participantEmail,
      name: participantEmail.split('@')[0]
    }]);
    setParticipantEmail('');
  };

  const removeParticipant = (id) => {
    setParticipants(participants.filter(p => p.id !== id));
    setItems(items.map(item => ({
      ...item,
      assignedTo: item.assignedTo.filter(pid => pid !== id)
    })));
  };

  const loadGroupMembers = (groupId) => {
    const group = groups.find(g => g.group_id === groupId);
    if (group) {
      setSelectedGroup(groupId);
      const members = group.members
        .filter(m => m.user_id !== user.user_id)
        .map(m => ({
          id: m.user_id || `email_${m.email}`,
          email: m.email,
          name: m.name
        }));
      setParticipants(members);
    }
  };

  const calculateSplits = () => {
    const allParticipants = [{ id: user.user_id, email: user.email, name: user.name }, ...participants];
    
    if (splitType === 'equal') {
      const share = totalAmount / allParticipants.length;
      return allParticipants.map(p => ({ ...p, amount: share }));
    } else {
      const totals = {};
      allParticipants.forEach(p => totals[p.id] = 0);
      
      items.forEach(item => {
        if (item.assignedTo.length > 0) {
          const share = (item.price * (item.quantity || 1)) / item.assignedTo.length;
          item.assignedTo.forEach(pid => {
            totals[pid] = (totals[pid] || 0) + share;
          });
        }
      });
      
      const taxPerPerson = taxesAndTip / allParticipants.length;
      
      return allParticipants.map(p => ({ ...p, amount: (totals[p.id] || 0) + taxPerPerson }));
    }
  };

  const handleCreateExpense = async () => {
    if (!title) {
      toast.error('Please enter a title');
      return;
    }
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    setCreating(true);
    try {
      // Helper function to convert frontend IDs to emails for backend
      const mapIdToEmail = (id) => {
        if (id === user.user_id) return user.email;
        const participant = participants.find(p => p.id === id);
        return participant ? participant.email : id;
      };

      const expenseData = {
        title,
        merchant,
        total_amount: itemsTotal,
        items: items.map(item => ({
          name: item.name,
          price: parseFloat(item.price) || 0,
          quantity: parseInt(item.quantity) || 1,
          // Convert frontend IDs to email addresses for backend processing
          assigned_to: item.assignedTo.map(mapIdToEmail)
        })),
        group_id: selectedGroup || null,
        split_type: splitType,
        date,
        time: time || null,
        participants: participants.map(p => p.email),
        currency,
        cgst: parseFloat(cgst) || 0,
        sgst: parseFloat(sgst) || 0,
        tip: parseFloat(tip) || 0
      };
      
      const res = await createExpense(expenseData);
      setCreatedExpense(res.data);
      setStep(3);
      toast.success('Expense created! 🎉');
    } catch (error) {
      toast.error('Failed to create expense');
    } finally {
      setCreating(false);
    }
  };

  const handleSendRequests = async () => {
    if (!createdExpense) return;
    
    const emailsToSend = participants.map(p => p.email);
    if (emailsToSend.length === 0) {
      toast.info('No participants to send requests to');
      navigate(`/expense/${createdExpense.expense_id}`);
      return;
    }
    
    setSending(true);
    try {
      await sendSplitRequest({
        expense_id: createdExpense.expense_id,
        recipient_emails: emailsToSend
      });
      toast.success('Split requests sent! 📧');
      navigate(`/expense/${createdExpense.expense_id}`);
    } catch (error) {
      toast.error('Some emails may have failed to send');
      navigate(`/expense/${createdExpense.expense_id}`);
    } finally {
      setSending(false);
    }
  };

  const splits = calculateSplits();

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 50%, #fff7ed 100%)'}}>
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-mesh pointer-events-none" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-xl border-b border-orange-100 z-40 flex items-center px-4 lg:px-8 shadow-sm">
        <div className="max-w-4xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-orange-50 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <h1 className="text-xl font-semibold font-['Poppins'] text-slate-800">New Split</h1>
          </div>
          
          {/* Step indicator */}
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step >= s 
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg' 
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 px-4 lg:px-8 pb-32 relative z-10">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Add Items */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg">
                  <h2 className="text-xl font-semibold font-['Poppins'] mb-6 text-slate-800">Receipt Details</h2>
                  
                  {/* Receipt Scanner */}
                  <div
                    {...getRootProps()}
                    className={`receipt-preview cursor-pointer mb-6 transition-all ${
                      isDragActive ? 'border-orange-500 bg-orange-50' : 'hover:border-orange-300'
                    }`}
                    data-testid="receipt-dropzone"
                  >
                    <input {...getInputProps()} />
                    {scanning ? (
                      <div className="py-8">
                        <Loader2 className="w-12 h-12 mx-auto mb-4 text-orange-500 animate-spin" />
                        <p className="text-slate-600">Scanning receipt with AI...</p>
                        <p className="text-sm text-slate-400 mt-1">Extracting items, GST & more</p>
                      </div>
                    ) : (
                      <div className="py-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                          <Camera className="w-8 h-8 text-orange-600" />
                        </div>
                        <p className="text-slate-700 mb-2 font-medium">Drag & drop a receipt image</p>
                        <p className="text-slate-500 text-sm">or click to browse</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="space-y-2">
                      <Label className="text-slate-700">Title *</Label>
                      <Input
                        placeholder="e.g., Dinner at Taj"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="h-12 bg-white border-slate-200 rounded-xl"
                        data-testid="expense-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Restaurant</Label>
                      <Input
                        placeholder="Restaurant name"
                        value={merchant}
                        onChange={(e) => setMerchant(e.target.value)}
                        className="h-12 bg-white border-slate-200 rounded-xl"
                        data-testid="expense-merchant-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Date</Label>
                      <Input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="h-12 bg-white border-slate-200 rounded-xl"
                        data-testid="expense-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Time</Label>
                      <Input
                        type="time"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        className="h-12 bg-white border-slate-200 rounded-xl"
                        data-testid="expense-time-input"
                      />
                    </div>
                  </div>
                  
                  {/* Currency Selector */}
                  <div className="mb-6">
                    <Label className="text-slate-700 mb-2 block">Currency</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger className="w-48 h-12 bg-white border-slate-200 rounded-xl" data-testid="currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        {CURRENCIES.map(c => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.symbol} {c.code} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Items */}
                <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold font-['Poppins'] text-slate-800">Items</h2>
                    <Button onClick={addItem} variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-50 rounded-full" data-testid="add-item-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                  </div>
                  
                  {items.length > 0 ? (
                    <div className="space-y-3">
                      {items.map((item, index) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-3 p-4 rounded-xl bg-slate-50"
                          data-testid={`item-row-${index}`}
                        >
                          <Input
                            placeholder="Item name"
                            value={item.name}
                            onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                            className="flex-1 h-10 bg-white border-slate-200 rounded-lg"
                          />
                          <div className="relative w-20">
                            <Input
                              type="number"
                              min="1"
                              placeholder="Qty"
                              value={item.quantity || ''}
                              onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                              className="h-10 bg-white border-slate-200 rounded-lg text-center"
                            />
                          </div>
                          <div className="relative w-32">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{currencySymbol}</span>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={item.price || ''}
                              onChange={(e) => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                              className="pl-8 h-10 bg-white border-slate-200 rounded-lg"
                            />
                          </div>
                          <button
                            onClick={() => removeItem(item.id)}
                            className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Utensils className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No items yet. Scan a receipt or add manually.</p>
                    </div>
                  )}
                </div>

                {/* Taxes & Tip */}
                <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg">
                  <h2 className="text-xl font-semibold font-['Poppins'] mb-6 text-slate-800">Taxes & Tip</h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700">CGST</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{currencySymbol}</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={cgst || ''}
                          onChange={(e) => setCgst(parseFloat(e.target.value) || 0)}
                          className="pl-8 h-12 bg-white border-slate-200 rounded-xl"
                          data-testid="cgst-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">SGST</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{currencySymbol}</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={sgst || ''}
                          onChange={(e) => setSgst(parseFloat(e.target.value) || 0)}
                          className="pl-8 h-12 bg-white border-slate-200 rounded-xl"
                          data-testid="sgst-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Tip / Service Charge</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{currencySymbol}</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={tip || ''}
                          onChange={(e) => setTip(parseFloat(e.target.value) || 0)}
                          className="pl-8 h-12 bg-white border-slate-200 rounded-xl"
                          data-testid="tip-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Total Summary */}
                  <div className="mt-6 pt-6 border-t border-slate-100 space-y-2">
                    <div className="flex justify-between text-slate-600">
                      <span>Items Subtotal</span>
                      <span>{currencySymbol}{itemsTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>CGST + SGST</span>
                      <span>{currencySymbol}{(parseFloat(cgst || 0) + parseFloat(sgst || 0)).toFixed(2)}</span>
                    </div>
                    {tip > 0 && (
                      <div className="flex justify-between text-slate-600">
                        <span>Tip</span>
                        <span>{currencySymbol}{parseFloat(tip || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold text-slate-800 pt-2">
                      <span>Grand Total</span>
                      <span data-testid="total-amount">{currencySymbol}{totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={items.length === 0 || !title}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full px-8 h-12 shadow-lg"
                    data-testid="next-step-btn"
                  >
                    Next: Add People
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Assign People */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg">
                  <h2 className="text-xl font-semibold font-['Poppins'] mb-6 text-slate-800">Add Participants</h2>
                  
                  {groups.length > 0 && (
                    <div className="mb-6">
                      <Label className="text-slate-700 mb-2 block">Load from Group</Label>
                      <Select value={selectedGroup} onValueChange={loadGroupMembers}>
                        <SelectTrigger className="h-12 bg-white border-slate-200 rounded-xl" data-testid="group-select">
                          <SelectValue placeholder="Select a group" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          {groups.map(group => (
                            <SelectItem key={group.group_id} value={group.group_id}>
                              {group.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="flex gap-2 mb-4">
                    <Input
                      type="email"
                      placeholder="Enter email to add participant"
                      value={participantEmail}
                      onChange={(e) => setParticipantEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addParticipant()}
                      className="h-12 bg-white border-slate-200 rounded-xl flex-1"
                      data-testid="participant-email-input"
                    />
                    <Button onClick={addParticipant} className="h-12 bg-orange-500 hover:bg-orange-600 rounded-xl px-6" data-testid="add-participant-btn">
                      <Plus className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {/* Current user */}
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-orange-50 border border-orange-200">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-medium shadow-md">
                        {user?.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{user?.name} (You)</p>
                        <p className="text-sm text-slate-500">{user?.email}</p>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-600">Paid</span>
                    </div>
                    
                    {participants.map((participant, index) => (
                      <motion.div
                        key={participant.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-50"
                        data-testid={`participant-${index}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-medium shadow-md">
                          {participant.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{participant.name}</p>
                          <p className="text-sm text-slate-500">{participant.email}</p>
                        </div>
                        <button
                          onClick={() => removeParticipant(participant.id)}
                          className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Split Type */}
                <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg">
                  <h2 className="text-xl font-semibold font-['Poppins'] mb-6 text-slate-800">Split Method</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      onClick={() => setSplitType('equal')}
                      className={`p-6 rounded-2xl border-2 transition-all ${
                        splitType === 'equal' 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-slate-200 hover:border-orange-200'
                      }`}
                      data-testid="split-equal-btn"
                    >
                      <Calculator className="w-8 h-8 text-orange-500 mb-3" />
                      <h3 className="font-semibold mb-1 text-slate-800">Split Equally</h3>
                      <p className="text-sm text-slate-500">Everyone pays the same amount</p>
                    </button>
                    
                    <button
                      onClick={() => setSplitType('by_item')}
                      className={`p-6 rounded-2xl border-2 transition-all ${
                        splitType === 'by_item' 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-slate-200 hover:border-orange-200'
                      }`}
                      data-testid="split-byitem-btn"
                    >
                      <Utensils className="w-8 h-8 text-green-500 mb-3" />
                      <h3 className="font-semibold mb-1 text-slate-800">Split by Items</h3>
                      <p className="text-sm text-slate-500">Assign specific items to people</p>
                    </button>
                  </div>
                </div>

                {/* Item Assignment */}
                {splitType === 'by_item' && participants.length > 0 && (
                  <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg">
                    <h2 className="text-xl font-semibold font-['Poppins'] mb-2 text-slate-800">Assign Items</h2>
                    <p className="text-slate-500 text-sm mb-6">Tick which items each person had</p>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-2 text-slate-500 font-medium">Item</th>
                            <th className="text-center py-3 px-2 text-slate-500 font-medium">You</th>
                            {participants.map(p => (
                              <th key={p.id} className="text-center py-3 px-2 text-slate-500 font-medium">
                                {p.name?.split(' ')[0]}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, index) => (
                            <tr key={item.id} className="border-b border-slate-100">
                              <td className="py-3 px-2">
                                <span className="text-slate-800">{item.name || `Item ${index + 1}`}</span>
                                <span className="text-slate-400 ml-2">{currencySymbol}{(item.price * (item.quantity || 1)).toFixed(2)}</span>
                              </td>
                              <td className="text-center py-3 px-2">
                                <Checkbox
                                  checked={item.assignedTo.includes(user.user_id)}
                                  onCheckedChange={() => toggleItemAssignment(item.id, user.user_id)}
                                  className="border-slate-300 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                                />
                              </td>
                              {participants.map(p => (
                                <td key={p.id} className="text-center py-3 px-2">
                                  <Checkbox
                                    checked={item.assignedTo.includes(p.id)}
                                    onCheckedChange={() => toggleItemAssignment(item.id, p.id)}
                                    className="border-slate-300 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                                    data-testid={`assign-${index}-${p.id}`}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Preview Splits */}
                <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg">
                  <h2 className="text-xl font-semibold font-['Poppins'] mb-6 text-slate-800">Split Preview</h2>
                  <div className="space-y-3">
                    {splits.map((split, index) => (
                      <div key={split.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium shadow-md ${
                            split.id === user.user_id 
                              ? 'bg-gradient-to-br from-orange-500 to-red-500' 
                              : 'bg-gradient-to-br from-green-500 to-emerald-500'
                          }`}>
                            {split.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-800">{split.name}</span>
                        </div>
                        <span className={`font-semibold ${
                          split.id === user.user_id ? 'text-orange-500' : 'text-green-600'
                        }`}>
                          {currencySymbol}{split.amount?.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <Button
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="border-slate-200 hover:bg-slate-50 rounded-full px-8 h-12"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleCreateExpense}
                    disabled={creating}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full px-8 h-12 shadow-lg"
                    data-testid="create-expense-btn"
                  >
                    {creating ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Create Split'
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Send Requests */}
            {step === 3 && createdExpense && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-lg mx-auto"
              >
                <div className="bg-white/90 backdrop-blur rounded-3xl p-8 text-center border border-orange-100 shadow-xl">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  
                  <h2 className="text-2xl font-bold font-['Poppins'] mb-2 text-slate-800">Split Created! 🎉</h2>
                  <p className="text-slate-500 mb-8">
                    {createdExpense.title} - {currencySymbol}{createdExpense.total_amount?.toFixed(2)}
                  </p>
                  
                  {participants.length > 0 && (
                    <div className="bg-slate-50 rounded-2xl p-6 mb-6 text-left">
                      <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-800">
                        <Mail className="w-5 h-5 text-orange-500" />
                        Send Payment Requests
                      </h3>
                      <div className="space-y-2">
                        {participants.map((p, index) => {
                          const split = createdExpense.splits?.find(s => s.email === p.email);
                          return (
                            <div key={p.id} className="flex items-center justify-between py-2">
                              <span className="text-slate-600">{p.email}</span>
                              <span className="text-green-600 font-medium">{currencySymbol}{split?.amount?.toFixed(2) || '0.00'}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {participants.length > 0 && (
                      <Button
                        onClick={handleSendRequests}
                        disabled={sending}
                        className="w-full h-14 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl text-lg font-semibold shadow-lg"
                        data-testid="send-requests-btn"
                      >
                        {sending ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Mail className="w-5 h-5 mr-2" />
                            Send Split Requests
                          </>
                        )}
                      </Button>
                    )}
                    
                    <Link to={`/expense/${createdExpense.expense_id}`}>
                      <Button variant="outline" className="w-full h-14 border-slate-200 hover:bg-slate-50 rounded-xl" data-testid="view-expense-btn">
                        View Expense Details
                      </Button>
                    </Link>
                    
                    <Link to="/dashboard">
                      <Button variant="ghost" className="w-full text-slate-500 hover:text-slate-700">
                        Back to Dashboard
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default CreateSplit;
